import React, { Fragment, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap";
import { Breadcrumbs, H5 } from "../../AbstractElements";
import { getPackageById, deletePackage, updatePackage } from "../../api/package.api";
import { toast } from "react-toastify";
import { titleCase } from "../../utils/case";
import { hasRole } from "../../utils/auth"

const PackageView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canManagePackages = hasRole(
    "superadmin",
    "finance"
  );
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [record, setRecord] = useState(null);

  const parsePricing = (pricing) => {
    if (!pricing) return [];

    if (typeof pricing === "string") {
      try {
        return JSON.parse(pricing);
      } catch {
        return [];
      }
    }

    return Array.isArray(pricing) ? pricing : [];
  };

  const fetchPackage = async () => {
    try {
      const data = await getPackageById(id);
      setRecord(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackage();
  }, [id]);

  if (loading) {
    return <div className="text-center mt-5">{titleCase(t("loading"))}...</div>;
  }

  if (!record) {
    return <div className="text-center mt-5">{titleCase(t("package not found"))}</div>;
  }

  const pricingRows = parsePricing(record.pricing);
  const packageItems = record.items || record.packageItems || [];

  const lotItems = packageItems.filter((item) => item.itemType === "lot");
  const includedAddOns = packageItems.filter(
    item =>
      item.itemType === "add_on" &&
      item.selectionType !== "optional"
  );

  const optionalAddOns = packageItems.filter(
    item =>
      item.itemType === "add_on" &&
      item.selectionType === "optional"
  );

  const getPrice = (season) => {
    const found = pricingRows.find((item) => item.season === season);
    return found?.amount !== undefined && found?.amount !== null
      ? `RM ${Number(found.amount).toFixed(2)}`
      : "-";
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("packages"))}
        title={titleCase(t("view package"))}
        mainTitle={
          <div className="d-flex align-items-center gap-2">
            <span>{record.name}</span>
            <Badge color={record.status === "active" ? "success" : "dark"}>
              {titleCase(t(record.status || "inactive"))}
            </Badge>
          </div>
        }
      />

      <Container fluid>
        <Row className="mb-4">
          <Col className="d-flex justify-content-end gap-2 flex-wrap">
            {canManagePackages && (
              <>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => navigate(`/packages/edit/${id}`)}
                >
                  {titleCase(t("edit"))}
                </button>

                <button
                  className="btn btn-sm btn-danger"
                  disabled={deleting}
                  onClick={async () => {
                    const confirmed = window.confirm(
                      t("are you sure you want to delete this package?")
                    );

                    if (!confirmed) return;

                    try {
                      setDeleting(true);
                      await deletePackage(id);
                      toast.success(t("deleted successfully"));
                      navigate("/packages");
                    } catch (err) {
                      toast.error(err.message);
                    } finally {
                      setDeleting(false);
                    }
                  }}
                >
                  {deleting ? t("deleting...") : titleCase(t("delete"))}
                </button>

                <button
                  className={`btn btn-sm ${
                    record.status === "active"
                      ? "btn-warning"
                      : "btn-success"
                  }`}
                  onClick={async () => {
                    try {
                      const confirmed = window.confirm(
                        record.status === "active"
                          ? t("are you sure you want to deactivate this package?")
                          : t("are you sure you want to activate this package?")
                      );

                      if (!confirmed) return;

                      await updatePackage(id, {
                        status:
                          record.status === "active"
                            ? "inactive"
                            : "active",
                      });

                      toast.success(
                        record.status === "active"
                          ? t("package deactivated successfully")
                          : t("package activated successfully")
                      );

                      fetchPackage();
                    } catch (err) {
                      toast.error(err.message);
                    }
                  }}
                >
                  {record.status === "active"
                    ? titleCase(t("deactivate"))
                    : titleCase(t("activate"))}
                </button>
              </>
            )}

            <button
              className="btn btn-sm btn-dark"
              onClick={() => navigate(-1)}
            >
              {titleCase(t("back"))}
            </button>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md="4">
            <Card className="mb-4 shadow-sm h-100">
              <CardHeader>
                <H5>{titleCase(t("package description"))}</H5>
              </CardHeader>

              <CardBody>
                <p className="text-muted mb-0">{record.description || "-"}</p>
              </CardBody>
            </Card>
          </Col>

          <Col md="4">
            <Card className="mb-4 shadow-sm h-100">
              <CardHeader>
                <H5>{titleCase(t("pricing"))}</H5>
              </CardHeader>

              <CardBody>
                <div className="p-3 border rounded mb-3">
                  <Row>
                    <Col md="6">
                      <p className="text-muted mb-0">
                        {titleCase(t("charged by"))}
                      </p>
                    </Col>
                    <Col md="6">
                      <h6 className="mb-0">
                        {titleCase(
                          t(record.pricing[0]?.chargedBy.replace(/_/g, " "))
                        )}
                      </h6>
                    </Col>
                  </Row>
                </div>
                <div className="p-3 border rounded mb-3">
                  <Row>
                    <Col md="6">
                      <p className="text-muted mb-0">
                        {titleCase(t("standard price"))}
                      </p>
                    </Col>
                    <Col md="6">
                      <h6 className="mb-0">{getPrice("non_peak")}</h6>
                    </Col>
                  </Row>
                </div>

                <div className="p-3 border rounded">
                  <Row>
                    <Col md="6">
                      <p className="text-muted mb-0">
                        {titleCase(t("peak season price"))}
                      </p>
                    </Col>
                    <Col md="6">
                      <h6 className="mb-0">{getPrice("peak")}</h6>
                    </Col>
                  </Row>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md="4">
            <Card className="mb-4 shadow-sm h-100">
              <CardHeader>
                <H5>{titleCase(t("summary"))}</H5>
              </CardHeader>

              <CardBody>
                <div className="p-3 border rounded mb-3">
                  <Row>
                    <Col md="6">
                      <p className="text-muted mb-0">
                        {titleCase(t("maximum lots allowed"))}
                      </p>
                    </Col>
                    <Col md="6">
                      <h6 className="mb-0">{record.maxLotAllowed ?? "-"}</h6>
                    </Col>
                  </Row>
                </div>
                <div className="p-3 border rounded mb-3">
                  <Row>
                    <Col md="6">
                      <p className="text-muted mb-0">{titleCase(t("capacity"))}</p>
                    </Col>
                    <Col md="6">
                      <h6 className="mb-0">{record.capacity ?? "-"}</h6>
                    </Col>
                  </Row>
                </div>

                <div className="p-3 border rounded">
                  <Row>
                    <Col md="6">
                      <p className="text-muted mb-0">{titleCase(t("add ons"))}</p>
                    </Col>
                    <Col md="6">
                      <h6 className="mb-0">{includedAddOns.length} Included / {optionalAddOns.length} Optional</h6>
                    </Col>
                  </Row>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Card className="mb-4 shadow-sm">
          <CardHeader>
            <H5>{titleCase(t("included items"))}</H5>
          </CardHeader>

          <CardBody>
            <Row>
              <Col md="6">
                <H5 className="mb-3">{titleCase(t("camp lots"))}</H5>

                {lotItems.length > 0 ? (
                  <div className="table-responsive">
                    <Table bordered>
                      <thead>
                        <tr>
                          <th>{titleCase(t("name"))}</th>
                          <th>{titleCase(t("location"))}</th>
                          <th>{titleCase(t("action"))}</th>
                        </tr>
                      </thead>

                      <tbody>
                        {lotItems.map((item) => (
                          <tr key={item.id}>
                            <td>{item.lot?.name || "-"}</td>
                            <td>{item.lot?.location || "-"}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() =>
                                  navigate(`/lots/view/${item.itemId}`)
                                }
                              >
                                {titleCase(t("view"))}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <p className="mb-0 text-muted">-</p>
                )}
              </Col>

              <Col md="6">
                <div className="mb-3">
                  <H5 className="mb-3">{titleCase(t("included add ons"))}</H5>

                  {includedAddOns.length > 0 ? (
                    <div className="table-responsive">
                      <Table bordered>
                        <thead>
                          <tr>
                            <th width="90">{titleCase(t("photo"))}</th>
                            <th>{titleCase(t("name"))}</th>
                            <th>{titleCase(t("category"))}</th>
                            <th width="100">{titleCase(t("quantity"))}</th>
                            <th>{titleCase(t("action"))}</th>
                          </tr>
                        </thead>

                        <tbody>
                          {includedAddOns.map((item) => (
                            <tr key={item.id}>
                              <td>
                                {item.addOn?.photo ? (
                                  <img
                                    src={`${process.env.REACT_APP_FILES_URL}/${item.addOn.photo}`}
                                    alt={item.addOn.name}
                                    style={{ width: 70, height: 70, objectFit: "contain", borderRadius: 8, border: "1px solid #ddd" }}
                                  />
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td>{item.addOn?.name || "-"}</td>
                              <td>{item.addOn?.category || "-"}</td>
                              <td className="text-center">
                                {item.quantity}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() =>
                                    navigate(`/inventory/view/${item.itemId}`)
                                  }
                                >
                                  {titleCase(t("view"))}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <p className="mb-0 text-muted">-</p>
                  )}
                </div>
                <div className="mt-3">
                  <H5 className="mb-3">{titleCase(t("optional add ons"))}</H5>

                  {optionalAddOns.length > 0 ? (
                    <div className="table-responsive">
                      <Table bordered>
                        <thead>
                          <tr>
                            <th width="90">{titleCase(t("photo"))}</th>
                            <th>{titleCase(t("name"))}</th>
                            <th>{titleCase(t("category"))}</th>
                            <th width="130">{titleCase(t("maximum quantity"))}</th>
                            <th>{titleCase(t("action"))}</th>
                          </tr>
                        </thead>

                        <tbody>
                          {optionalAddOns.map(item => (
                            <tr key={item.id}>
                              <td>
                                {item.addOn?.photo ? (
                                  <img
                                    src={`${process.env.REACT_APP_FILES_URL}/${item.addOn.photo}`}
                                    alt={item.addOn.name}
                                    style={{ width: 70, height: 70, objectFit: "contain", borderRadius: 8, border: "1px solid #ddd" }}
                                  />
                                ) : "-"}
                              </td>

                              <td>{item.addOn?.name || "-"}</td>
                              <td>{item.addOn?.category || "-"}</td>
                              <td className="text-center">{item.quantity}</td>

                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => navigate(`/inventory/view/${item.itemId}`)}
                                >
                                  {titleCase(t("view"))}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <p className="mb-0 text-muted">No optional add ons</p>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="mb-4 shadow-sm">
          <CardHeader>
            <H5>{titleCase(t("remarks"))}</H5>
          </CardHeader>

          <CardBody>
            {record.remark ? (
              <div dangerouslySetInnerHTML={{ __html: record.remark }} />
            ) : (
              <p className="mb-0 text-muted">-</p>
            )}
          </CardBody>
        </Card>
      </Container>
    </Fragment>
  );
};

export default PackageView;